import { useState } from "react";
import { PostgresResponseForMedicalPaOrders } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";

/** API response state for the generic auth status table, tracking data, loading, and errors. */
export interface GenericAuthStatusState {
  data?: PostgresResponseForMedicalPaOrders;
  loading?: boolean;
  error?: string;
  showinLineLoader?: boolean;
}

/**
 * Central state management hook for Generic Auth Status table data and API response state
 *
 * This is the foundation hook that creates and manages two primary state objects:
 * - tableData: Array of processed table records ready for display
 * - genericAuthStatusState: API response metadata (loading, error, pagination info)
 *
 * @returns {Object} State container with table data and setters
 * @returns {GenericAuthStatusTableData[]} returns.tableData - Array of formatted table records
 * @returns {Function} returns.setTableData - Setter for table data
 * @returns {GenericAuthStatusState} returns.genericAuthStatusState - API response state
 * @returns {Function} returns.setGenericAuthStatusState - Setter for API state
 *
 * @example
 * const { tableData, setTableData, genericAuthStatusState, setGenericAuthStatusState } =
 *   useGenericAuthStatusTableData();
 */
export const useGenericAuthStatusTableData = () => {
  const [tableData, setTableData] = useState<GenericAuthStatusTableData[]>([]);
  const [genericAuthStatusState, setGenericAuthStatusState] =
    useState<GenericAuthStatusState>({
      data: undefined,
      loading: false,
      error: undefined,
      showinLineLoader: false,
    });

  return {
    genericAuthStatusState,
    setGenericAuthStatusState,
    tableData,
    setTableData,
  };
};
